import React from 'react';
import PropTypes from 'prop-types';
import { EditorWrapper, EditorPlaceholder, NoContentMessage, LoadingMessage } from './baseComponents';
import FloatingMenu from './menus/floatingMenu/';
import Prompt, { PromptOverlay } from './prompts/Prompt';
import TopMenu from './menus/topMenu/';
import useFloatingMenuPlugin from './menus/floatingMenu/useFloatingMenuPlugin';
import usePromptMenuPlugin from './hooks/usePromptMenuPlugin';
import useProsemirror from './hooks/useProsemirror';
import useTopMenuPlugin from './menus/topMenu/useTopMenuPlugin';
import useUIStatePlugin from './hooks/useUIStatePlugin';
import useAnnotationPlugin from './hooks/useAnnotationPlugin';
import { richSchema } from '../editor-core';
import useWordCount from './hooks/useWordCount';
import CharacterLimit from './CharacterLimit';

export default function Editor({
  roomName,
  onDispatchTransaction,
  consumerApi,
  minHeight='400px',
  characterLimit = undefined,
  style = undefined,
  contentType="text",
  defaultContent='',
}) {

  const { editorReady, editorViewRef, editorEl, editorState, editorLoading } = useProsemirror(
    roomName,
    onDispatchTransaction,
    {
      schema: richSchema,
      consumerApi,
      menus: {
        topMenu: true,
        promptMenu: true,
        floatingMenu: true
      }
    }
  );

  const { menuItems } = useTopMenuPlugin(editorReady, editorViewRef);
  const { showPrompt, closePrompt } = usePromptMenuPlugin(editorReady, editorViewRef, editorState);
  const floatingMenuPluginState = useFloatingMenuPlugin(editorReady, editorViewRef);
  const { readOnly, showDiff, historyMode } = useUIStatePlugin(editorState);
  useAnnotationPlugin(editorReady, editorViewRef, editorState, consumerApi, historyMode);
  const wordCount = useWordCount(editorEl);
  const noContent = !editorLoading && editorEl.current && editorEl.current.innerText.length === 0 && !defaultContent;
  const bodyEditor = roomName.match(/rich-body$/);

  return (
    <React.Fragment>
      <EditorWrapper
        showDiff={showDiff}
        readOnly={readOnly}
        historyMode={historyMode}
        minHeight={minHeight}
        className={'read-only-body'}
        style={style}
      >
        {showPrompt && <PromptOverlay />}
        {editorReady && !readOnly && !historyMode && <TopMenu items={menuItems} />}
        <EditorPlaceholder id="editor-ui-editor" ref={editorEl}></EditorPlaceholder>
        {!!showPrompt && <Prompt pluginItem={showPrompt} onClose={closePrompt} />}
        {floatingMenuPluginState.show && (
          <FloatingMenu pluginState={floatingMenuPluginState} boundingEl={editorEl.current} />
        )}
      </EditorWrapper>

      {editorLoading && <LoadingMessage />}
      {noContent && <NoContentMessage contentType={contentType} />}
      {characterLimit && <CharacterLimit limit={characterLimit} editorEl={editorEl} />}
      {!!wordCount && bodyEditor && <p>Word count: <span id="word-count">{wordCount}</span></p>}
    </React.Fragment>
  );
}

Editor.defaultProps = {
  minHeight: '400px',
  characterLimit: undefined,
  contentType: 'text',
  style: undefined,
  defaultContent: '',
}

Editor.propTypes = {
  roomName: PropTypes.string.isRequired,
  onDispatchTransaction: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  consumerApi: PropTypes.object.isRequired,
  minHeight: PropTypes.string,
  characterLimit: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
  contentType: PropTypes.string,
  defaultContent: PropTypes.string,
};

export { richSchema };



// WEBPACK FOOTER //
// ./app/javascript/client/editor-ui/Editor.jsx